import { Component, h, Prop, State, Listen, Element } from '@stencil/core';

@Component({
  tag: 'bubble-links',
  styleUrl: 'bubble-links.scss',
  shadow: false,
})
export class BubbleLinks {
  @Prop() links: any[];
  @Prop() bubbleColor: string;
  @Prop() accessibility: { pauseBubbleAnimation: string; playBubbleAnimation: string; nextBubble: string; previousBubble: string };  @Element() el: HTMLElement;
  @State() isPaused: boolean = false;
  @State() isHoverPaused: boolean = false;
  @State() position: number = 0;
  @State() screenWidth: number = 0;

  

  private readonly SCROLL_SPEED = 0.3;
  private scrollTimer: number;
  private readonly STEP_SIZE = 100;
  private containerHeight: number = 0;
  private totalHeight: number = 0;
  private previousFocusedElement: HTMLElement | null = null;
  private startY: number = 0;
  private currentY: number = 0;
  private isDragging: boolean = false;

  componentWillLoad() {
    this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    window.addEventListener('resize', this.debounce(this.resizeEventHandler, 300));
  } 

  resizeEventHandler = () => {
    this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  };

  private debounce(func: Function, wait: number) {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  componentDidLoad() {    
    this.el.addEventListener('focusin', this.handleFocusIn);
    const container = this.el.querySelector('.vs-bubble-links-container') as HTMLElement;
    if (!container) return;

    const additionalLinks1 = this.links.map(link => this.createVsLinkClone(link));
    const additionalLinks2 = this.links.map(link => this.createVsLinkClone(link));
    
    // Append the new sets to container
    additionalLinks1.reverse().forEach(link => container.insertBefore(link, container.firstChild));
    additionalLinks2.forEach(link => container.appendChild(link));

    this.containerHeight = container.clientHeight / 3;
    this.totalHeight = container.clientHeight;

    container.style.transform = `translateY(${this.position}px)`;

    container.addEventListener('pointerdown', this.onPointerDown);
    container.addEventListener('pointermove', this.onPointerMove);
    container.addEventListener('pointerup', this.onPointerUp);
    container.addEventListener('pointercancel', this.onPointerUp);

    container.addEventListener('touchstart', this.onTouchStart);
    container.addEventListener('touchmove', this.onTouchMove);
    container.addEventListener('touchend', this.onTouchEnd);

    this.el.addEventListener('mousedown', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('vs-link')) {
        e.preventDefault();
      }
    });

    setTimeout(() => {
      this.startAnimation();
    }, 1000); 
    this.setupKeyboardNavigation();
    this.setupHoverListeners();

  const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Tab') {
    event.preventDefault();
    if(!event.shiftKey){
    this.isPaused = true;
    }

    const focusableElements = this.getFocusableElements();
    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    const isShiftPressed = event.shiftKey;
    const isFirstElement = currentIndex === 0;
    const isLastElement = currentIndex === focusableElements.length - 1;

    // Check if focus is coming from outside the component
    if (this.previousFocusedElement && !this.el.contains(this.previousFocusedElement)) {
      // Reset the previousFocusedElement
      this.previousFocusedElement = null;
      return;
    }

    // Handle exit conditions
    if (isLastElement && !isShiftPressed) {
      const closestParent = this.el.closest('.marquee-container') as HTMLElement;
      const nextFocusableElement = this.findNextFocusableElement(closestParent);
            
    
      if (nextFocusableElement) {
        (nextFocusableElement as HTMLElement).focus();
      }
      return;
    }

    if (isFirstElement && isShiftPressed) {
      const previousFocusableElement = this.findPreviousFocusableElement(this.el);
      if (previousFocusableElement) {
        this.previousFocusedElement = document.activeElement as HTMLElement; // Store the previous focused element
        (previousFocusableElement as HTMLElement).focus();
      }
      return;
    }

    // Handle normal focus movement
    const nextIndex = isShiftPressed
    ? (currentIndex - 1 + focusableElements.length) % focusableElements.length
    : (currentIndex + 1) % focusableElements.length;
  
  const nextElement = focusableElements[nextIndex];
    nextElement.focus();
    nextElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};
    
    this.el.addEventListener('keydown', handleKeyDown);
    this.el.addEventListener('shiftTabPressed', handleKeyDown);
    
    this.el.addEventListener('focusout', (event) => {
      this.previousFocusedElement = event.target as HTMLElement;
    });
  }


  private handleFocusIn = (event: FocusEvent) => {
    const targetElement = event.target as HTMLElement;
    if (targetElement.tagName.toLowerCase() === 'a' && targetElement.parentElement?.classList.contains('vs-bubble')) {
      const parentElement = targetElement.parentElement;
        const middleElements = parentElement.parentElement.querySelectorAll('[data-middle="true"]');
            (middleElements[middleElements.length-1] as HTMLElement).focus();
            return;
          }
        }

  private onTouchStart = (event: TouchEvent) => {
    this.startY = event.touches[0].clientY;
    this.isDragging = true;
  };
  
  private onTouchMove = (event: TouchEvent) => {
    if (event.touches.length === 0 || !this.isDragging) return;
    this.currentY = event.touches[0].clientY;
    const deltaY = this.currentY - this.startY;
    this.startY = this.currentY;
  
    // Update position
    this.position += deltaY;
  
    // Infinite scroll logic
    if (this.position > 0) {
      this.position = -this.totalHeight / 2;
    } else if (this.position < -this.totalHeight / 2) {
      this.position = 0;
    }
  
    this.updatePosition();
  };
  
  private onTouchEnd = () => {
    this.isDragging = false;
  };

  private onPointerDown = (event: PointerEvent) => {
    this.startY = event.clientY;
    this.isDragging = true;
  };

  private onPointerMove = (event: PointerEvent) => {
    if (!this.isDragging) return;
    this.currentY = event.clientY;
    const deltaY = this.currentY - this.startY;
    this.startY = this.currentY;

    // Update position
    this.position += deltaY;

    // Infinite scroll logic
    if (this.position > 0) {
      this.position = -this.totalHeight / 2;
    } else if (this.position < -this.totalHeight / 2) {
      this.position = 0;
    }

    this.updatePosition();
  };

  private onPointerUp = () => {
    this.isDragging = false;
  };

  private updatePosition() {
    const container = this.el.querySelector('.vs-bubble-links-container') as HTMLElement;
    if (container) {
      container.style.transform = `translateY(${this.position}px)`;
    }    
  }
  

  private createVsLinkClone(linkData: any): HTMLElement {
    const vsLink = document.createElement('vs-link');

    // Set all the properties from the original link data
    vsLink.setAttribute('class', `vs-bubble ${this.bubbleColor}`);
    vsLink.setAttribute('link-style', "link");
    vsLink.setAttribute('link-type', linkData.linkType);
    vsLink.setAttribute('button-color', linkData.ctaButtonColor);
    vsLink.setAttribute('link-text', linkData.ctaTitle);
    vsLink.setAttribute('link-target', linkData.target);
    vsLink.setAttribute('link-conversion', linkData.conversionType);
    vsLink.setAttribute('link-val', linkData.link);
    vsLink.setAttribute('link-aria-label', linkData.ctaLinkAriaLabel);
    vsLink.setAttribute('has-dark-bg', linkData.ctaHasDarkBg);
    vsLink.setAttribute('aria-hidden', "true");

    // Optional properties - only set if they exist in the original data
    if (linkData.ctaVideoImagePath !== undefined) vsLink.setAttribute('img-path', linkData.ctaVideoImagePath);
if (linkData.ctaVideoUrl !== undefined) vsLink.setAttribute('video-url', linkData.ctaVideoUrl);
if (linkData.ctaVideoTitle !== undefined) vsLink.setAttribute('video-title', linkData.ctaVideoTitle);
if (linkData.modalTarget !== undefined) vsLink.setAttribute('modal-target', linkData.modalTarget);
if (linkData.isNoFollow !== undefined) vsLink.setAttribute('is-no-follow', linkData.isNoFollow);
vsLink.addEventListener('click', (e) => this.handleBubbleLinkClick(e.target as HTMLAnchorElement));

    return vsLink;
  }

  private findPreviousFocusableElement(currentElement: HTMLElement) {
    const focusableSelectors = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    let parent = currentElement.parentElement;

    while (parent) {
      let sibling = parent.previousElementSibling;

      while (sibling) {
        const focusableElements = Array.from(sibling.querySelectorAll(focusableSelectors)) as HTMLElement[];
        if (focusableElements.length > 0) {
          return focusableElements[focusableElements.length - 1]; // Return the last focusable element
        }
        sibling = sibling.previousElementSibling;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  disconnectedCallback() {
    if (this.scrollTimer) {
      cancelAnimationFrame(this.scrollTimer);
    }
    window.removeEventListener('resize', this.debounce(this.resizeEventHandler, 300));
  }

  private findNextFocusableElement(currentElement: HTMLElement) {
    const focusableSelectors = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    let sibling = currentElement.nextElementSibling;

    while (sibling) {
      // Special case for featured tablet components
      if (sibling.classList?.contains('vs-featured-tablet') || 
          sibling.querySelector('.vs-featured-tablet')) {
        
        // Find the vs-fc-s-wrapper-tablet element which contains visible focusable elements
        const wrapperTablet = sibling.querySelector('.vs-fc-s-wrapper-tablet');
        
        if (wrapperTablet) {
          // Find the first focusable element within this wrapper
          const focusableInWrapper = wrapperTablet.querySelector(focusableSelectors);
          if (focusableInWrapper) {
            return focusableInWrapper;
          }
        }
      }
      
      // Standard checks for other elements
      const focusable = sibling.matches(focusableSelectors) ?
        sibling :
        sibling.querySelector(focusableSelectors);

      if (focusable != null) {
        return focusable;
      }
      
      sibling = sibling.nextElementSibling;
    }
    
    return null;
  }

  private setupHoverListeners() {
    const bubbles = this.el.querySelectorAll('.vs-bubble-links-container .vs-bubble') as NodeListOf<HTMLElement>;
    bubbles.forEach(bubble => {
      bubble.addEventListener('mouseenter', this.handleMouseEnter);
      bubble.addEventListener('mouseleave', this.handleMouseLeave);
    });
  }

  private handleMouseEnter = () => {
    if (!this.isPaused) {
      this.isHoverPaused = this.isPaused = true;
    }
  }

  private handleMouseLeave = () => {
    if (this.isHoverPaused) {
      this.isPaused = false;
      this.isHoverPaused = false;
      this.startAnimation();
    }
  }

  private setupKeyboardNavigation() {
    const bubbles = this.el.querySelectorAll('.vs-bubble-links-container .vs-bubble') as NodeListOf<HTMLElement>;
  
    bubbles.forEach((bubble, index) => {
      if (index >= this.links.length && index < this.links.length * 2) {
        bubble.tabIndex = 0;
        bubble.setAttribute('data-middle', 'true');
      } else {
        bubble.tabIndex = -1;
      }
    });
  }

  private getFocusableElements() {

    const focusableElements: HTMLElement[] = [];
    const playPauseButton = this.el.querySelector('.vs-bubble-play-pause') as HTMLElement;
    const downButton = this.el.querySelector('.bubble-down') as HTMLElement;
    const upButton = this.el.querySelector('.bubble-up') as HTMLElement;

    if (playPauseButton) focusableElements.push(playPauseButton);

    if (this.isPaused) {
      if (upButton) upButton.tabIndex = 0;
      if (downButton) downButton.tabIndex = 0;
      if (downButton) focusableElements.push(downButton);
      if (upButton) focusableElements.push(upButton);
    }

    const middleBubbles = this.el.querySelectorAll('.vs-bubble-links-container .vs-bubble[data-middle="true"]') as NodeListOf<HTMLElement>;
    middleBubbles.forEach(bubble => focusableElements.push(bubble));

    return focusableElements;
  }

  private startAnimation = () => {
    if (this.isPaused || this.scrollTimer) return;

    const container = this.el.querySelector('.vs-bubble-links-container') as HTMLElement;
    if (!container) return;

    const animate = () => {
      if (this.isPaused) {
        this.scrollTimer = requestAnimationFrame(animate);
        return;
      }

      this.position -= this.SCROLL_SPEED;
      const LARGE_SCREEN_POSITION = 43;
      const SMALL_SCREEN_POSITION = 33;
      const MOBILE_SCREEN_POSITION = 2; 

      if (Math.abs(this.position) > this.containerHeight) {
        if(this.screenWidth> 1200){
          this.position = LARGE_SCREEN_POSITION;
        }
        else if(this.screenWidth <= 768){
          this.position = MOBILE_SCREEN_POSITION;
        }
        else{
          this.position = SMALL_SCREEN_POSITION;
        }
      }
      
      container.style.transform = `translateY(${this.position}px)`;

      this.scrollTimer = requestAnimationFrame(animate);
    };

    this.scrollTimer = requestAnimationFrame(animate);
  };

  @Listen('heroClicked', { target: 'body' })
  handleHeroClicked() {
    this.toggleAnimation();
  }

  private handleButtonClick = (e: Event, action: () => void) => {
    e.stopPropagation();
    action();
    this.updateFocusableElements();
  }

  private updateFocusableElements() {
    const focusableElements: HTMLElement[] = [];
    const playPauseButton = this.el.querySelector('.vs-bubble-play-pause') as HTMLElement;
    const upButton = this.el.querySelector('.bubble-up') as HTMLElement;
    const downButton = this.el.querySelector('.bubble-down') as HTMLElement;

    if (playPauseButton) focusableElements.push(playPauseButton);

    if (this.isPaused) {
      if (upButton) upButton.tabIndex = 0;
      if (downButton) downButton.tabIndex = 0;
      if (upButton) focusableElements.push(upButton);
      if (downButton) focusableElements.push(downButton);
    } else {
      if (upButton) upButton.tabIndex = -1;
      if (downButton) downButton.tabIndex = -1;
    }

    const middleBubbles = this.el.querySelectorAll('.vs-bubble-links-container .vs-bubble[data-middle="true"]') as NodeListOf<HTMLElement>;
    middleBubbles.forEach(bubble => focusableElements.push(bubble));
  }

  private toggleAnimation = () => {
    this.isPaused = !this.isPaused;
    this.updateFocusableElements();
    if (!this.isPaused) {
      if (this.scrollTimer) {
        cancelAnimationFrame(this.scrollTimer);
        this.scrollTimer = null;
      }
      this.startAnimation();
    }
  };

  private moveUp = () => {
    if (!this.isPaused) return;
    this.position -= this.STEP_SIZE;
    if (this.position <= -this.containerHeight) {
      this.position += this.containerHeight;
    }
    const container = this.el.querySelector('.vs-bubble-links-container') as HTMLElement;
    if (container) {
      container.style.transform = `translateY(${this.position}px)`;
    }
  }

  private moveDown = () => {
    if (!this.isPaused) return;
    this.position += this.STEP_SIZE;
    if (this.position >= 0) {
      this.position -= this.containerHeight;
    }
    const container = this.el.querySelector('.vs-bubble-links-container') as HTMLElement;
    if (container) {
      container.style.transform = `translateY(${this.position}px)`;
    }
  }

  handleBubbleLinkClick(el: HTMLAnchorElement): void {
    (el.firstChild as HTMLElement).click()
  }

  render() {

    const allLinks = [...this.links];
    return (
      <div class="vs-bubble-links-and-controller">
      <div class="vs-bubble-controls vs-d-flex">
        <button
          class={`vs-bubble-control-button vs-bubble-play-pause vs-bubble-play-pause-black ${this.isPaused && !this.isHoverPaused ? '' : 'is-playing'}`}
          onClick={(e) => this.handleButtonClick(e, this.toggleAnimation)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              this.toggleAnimation();
            }
          }}
          aria-label={this.isPaused ? `${this.accessibility.pauseBubbleAnimation}` : `${this.accessibility.playBubbleAnimation}`}
          >
          </button>
        {this.isPaused && !this.isHoverPaused && (
          <button
            class="visa-icon-arrow-down-md vs-bubble-control-button bubble-down bubble-down-black"
            onClick={(e) => this.handleButtonClick(e, this.moveDown)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                this.moveDown();
              }
            }}
            aria-label={this.accessibility.previousBubble}>
            </button>
        )}
        {this.isPaused && !this.isHoverPaused && (
          <button
            class="visa-icon-arrow-up-md vs-bubble-control-button bubble-up bubble-up-black"
            onClick={(e) => this.handleButtonClick(e, this.moveUp)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                this.moveUp();
              }
            }}
            aria-label={this.accessibility.nextBubble}>

          </button>
        )}
      </div>
        <div class="vs-bubble-links-controller">

          <div class="vs-bubble-links-container">
            {allLinks.map((link) => (
              <vs-link class={`vs-bubble ${this.bubbleColor}`}
  role='link'
  onClick={(e) => {
    this.handleBubbleLinkClick(e.currentTarget as HTMLAnchorElement);
  }}
  onKeyDown={(e) => {
    if (e.key === 'Enter') {
      this.handleBubbleLinkClick(e.currentTarget as HTMLAnchorElement);
    }
  }}
                linkStyle="link"
                linkType={link.linkType}
                buttonColor={link.ctaButtonColor}
                linkText={link.ctaTitle}
                linkTarget={link.target}
                linkConversion={link.conversionType}
                linkVal={link.link}
                linkAriaLabel={link.ctaLinkAriaLabel}
                hasDarkBg={link.ctaHasDarkBg}
                imgPath={link.ctaVideoImagePath}
                videoTitle={link.ctaVideoTitle}
                videoUrl={link.ctaVideoUrl}
                modalTarget={link.modalTarget}
                isNoFollow={link.ctaNoFollow}           
              ></vs-link>
            ))}
          </div>
        </div>
      </div>
    );
  }
}